
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { GuestInfoDialog } from "./GuestInfoDialog";

const taskFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  status: z.enum(["todo", "in_progress", "on_hold", "done"]),
  priority: z.enum(["low", "medium", "high"]),
  phase_id: z.string().optional(),
  assigned_to: z.string().optional(),
  start_date: z.date().nullable().optional(),
  due_date: z.date().nullable().optional(),
});

type TaskFormData = z.infer<typeof taskFormSchema>;

interface TaskFormProps {
  projectId: string;
  phases: any[];
  initialData?: Partial<TaskFormData>;
  onSuccess: () => void;
  isSharedView?: boolean;
  shareToken?: string;
  organizationId?: string;
}

export const TaskForm = ({ 
  projectId, 
  phases, 
  initialData, 
  onSuccess,
  isSharedView = false,
  shareToken,
  organizationId
}: TaskFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [guestInfo, setGuestInfo] = useState<{ name: string; email?: string } | null>(null);
  
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const form = useForm<TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      status: initialData?.status || "todo",
      priority: initialData?.priority || "medium",
      phase_id: initialData?.phase_id || undefined,
      assigned_to: initialData?.assigned_to || undefined,
      start_date: initialData?.start_date ? new Date(initialData.start_date) : null,
      due_date: initialData?.due_date ? new Date(initialData.due_date) : null,
    },
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (isSharedView) return;
      
      try {
        const orgId = currentOrganization?.id || organizationId;
        if (!orgId) return;

        // First, get organization members
        const { data: members, error: membersError } = await supabase
          .from('organization_members')
          .select('user_id')
          .eq('organization_id', orgId);

        if (membersError) {
          console.error('Error fetching members:', membersError);
          throw membersError;
        }

        if (!members?.length) {
          setUsers([]);
          return;
        }

        // Then, get profiles for those users
        const userIds = members.map(m => m.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        setUsers(profiles || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      }
    };

    fetchUsers();
  }, [currentOrganization?.id, organizationId, isSharedView]);

  const processTaskSubmission = async (data: TaskFormData, guestData?: { name: string; email?: string }) => {
    console.log('🔄 Starting task submission process', { 
      isSharedView, 
      hasShareToken: !!shareToken, 
      hasGuestData: !!guestData,
      data 
    });
    
    setIsLoading(true);
    
    try {
      const taskData = {
        ...data,
        start_date: data.start_date?.toISOString().split('T')[0] || null,
        due_date: data.due_date?.toISOString().split('T')[0] || null,
        // Convert empty strings to null for optional fields
        phase_id: data.phase_id || null,
        assigned_to: data.assigned_to || null,
      };

      console.log('📝 Prepared task data:', taskData);

      if (isSharedView && shareToken) {
        console.log('🌐 Processing shared view submission');
        
        const action = initialData?.title ? 'updateTask' : 'createTask';
        const finalGuestData = guestData || guestInfo || { name: 'Invitado Anónimo' };
        
        const body = action === 'updateTask' 
          ? { 
              action, 
              shareToken, 
              taskId: (initialData as any)?.id, 
              taskData,
              guestInfo: finalGuestData
            }
          : { 
              action, 
              shareToken, 
              taskData: { ...taskData, project_id: projectId },
              guestInfo: finalGuestData
            };

        console.log('📤 Sending shared project request:', body);

        const { data: response, error } = await supabase.functions.invoke('shared-project-operations', {
          body
        });

        console.log('📥 Shared project response:', response);

        if (error) {
          console.error('❌ Edge function error:', error);
          throw new Error(`Edge function error: ${error.message}`);
        }

        if (!response?.success) {
          console.error('❌ Edge function returned error:', response?.error);
          throw new Error(response?.error || 'Failed to save task');
        }

        toast({
          title: "Éxito",
          description: `Tarea ${initialData?.title ? "actualizada" : "creada"} correctamente`,
        });
      } else {
        console.log('🏢 Processing regular organization submission');
        
        const orgId = currentOrganization?.id || organizationId;
        if (!orgId) throw new Error("Organization ID required");

        const action = initialData?.title ? 'updateTask' : 'createTask';
        const body = action === 'updateTask'
          ? { action, taskId: (initialData as any)?.id, taskData, organizationId: orgId }
          : { action, taskData, projectId, organizationId: orgId };

        console.log('📤 Sending regular project request:', body);

        const { data: response, error } = await supabase.functions.invoke('project-tracking-operations', {
          body
        });

        console.log('📥 Regular project response:', response);

        if (error) throw error;
        if (!response?.success) throw new Error(response.error);

        toast({
          title: "Éxito",
          description: `Tarea ${initialData?.title ? "actualizada" : "creada"} correctamente`,
        });
      }

      form.reset();
      setGuestInfo(null);
      onSuccess();
    } catch (error: any) {
      console.error('❌ Error saving task:', error);
      toast({
        title: "Error",
        description: error?.message || "Error al guardar la tarea. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: TaskFormData) => {
    console.log('📝 Form submitted:', data);
    console.log('🔍 Current state:', { isSharedView, hasGuestInfo: !!guestInfo, isEdit: !!initialData?.title });

    // For shared views creating new tasks, show guest dialog first if no guest info
    if (isSharedView && !initialData?.title && !guestInfo) {
      console.log('👤 Showing guest dialog for new task in shared view');
      setShowGuestDialog(true);
      return;
    }

    // Process submission directly
    await processTaskSubmission(data);
  };

  const handleGuestInfoSubmit = async (info: { name: string; email?: string }) => {
    console.log('👤 Guest info submitted:', info);
    setGuestInfo(info);
    setShowGuestDialog(false);
    
    // Get current form values and process submission
    const formData = form.getValues();
    console.log('📝 Processing submission with guest info and form data:', formData);
    
    await processTaskSubmission(formData, info);
  };

  // Helper function to handle select value changes
  const handleSelectChange = (value: string, fieldName: string) => {
    // Convert special values back to undefined for form submission
    const formValue = ['no-phase', 'unassigned'].includes(value) ? undefined : value;
    form.setValue(fieldName as any, formValue);
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título</FormLabel>
                <FormControl>
                  <Input placeholder="Título de la tarea" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descripción</FormLabel>
                <FormControl>
                  <Textarea placeholder="Descripción de la tarea" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona estado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="todo">Por Hacer</SelectItem>
                      <SelectItem value="in_progress">En Progreso</SelectItem>
                      <SelectItem value="on_hold">En Pausa</SelectItem>
                      <SelectItem value="done">Completado</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridad</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona prioridad" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="phase_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fase</FormLabel>
                <Select 
                  onValueChange={(value) => handleSelectChange(value, 'phase_id')} 
                  value={field.value || "no-phase"}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona fase" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="no-phase">Sin Fase</SelectItem>
                    {phases.map((phase) => (
                      <SelectItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isSharedView && (
            <FormField
              control={form.control}
              name="assigned_to"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asignado a</FormLabel>
                  <Select 
                    onValueChange={(value) => handleSelectChange(value, 'assigned_to')} 
                    value={field.value || "unassigned"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona asignado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Sin asignar</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fecha de Inicio</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Selecciona fecha</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fecha de Vencimiento</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Selecciona fecha</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {initialData?.title ? "Actualizar Tarea" : "Crear Tarea"}
            </Button>
          </div>
        </form>
      </Form>

      <GuestInfoDialog
        open={showGuestDialog}
        onOpenChange={setShowGuestDialog}
        onSubmit={handleGuestInfoSubmit}
      />
    </>
  );
};
